import type { ReactNode, Ref } from "react";
import { forwardRef } from "react";

import classNames from "classnames";

import styles from "./NavPanel.module.scss";

type NavPanelProps = {
  id: string;
  isActive: boolean;
  isRoot?: boolean;
  ariaLabel?: string;
  labelledById?: string;
  className?: string;
  disableTransition?: boolean;
  children: ReactNode;
};

/**
 * Generic panel wrapper for a single navigation level.
 * All panels stay mounted in the DOM. `inert` and `aria-hidden` mirror the
 * active state so inactive panels are removed from the tab and a11y trees.
 */
export const NavPanel = forwardRef(function NavPanel(
  { id, isActive, isRoot = false, ariaLabel, labelledById, className, disableTransition, children }: NavPanelProps,
  ref: Ref<HTMLElement>
) {
  return (
    <section
      ref={ref}
      id={id}
      tabIndex={-1}
      inert={!isActive}
      aria-hidden={!isActive}
      aria-label={ariaLabel}
      aria-labelledby={labelledById}
      className={classNames(
        styles.panel,
        { [styles.root]: isRoot, [styles.active]: isActive, [styles.noTransition]: disableTransition },
        className
      )}
    >
      {children}
    </section>
  );
});
